import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { DeletedChildProfile } from '../../models/childProfile';
import { ChildProfileDataService, PurgeInventory } from '../../services/childProfileDataService';
import { formatDisplayDate } from '../../services/dateUtils';
import PasswordReauthModal from '../PasswordReauthModal';

interface HardDeleteModalProps {
    row: DeletedChildProfile;
    /** didDelete = true when the purge completed (caller refreshes the list). */
    onClose: (didDelete: boolean) => void;
}

type Step = 'inventory' | 'reauth' | 'running' | 'done' | 'error';

/**
 * Permanent-deletion flow (child-deletion epic, Phase 2). Steps:
 *   1. inventory — live counts of everything that will be destroyed, the
 *      typed-name confirmation, and (before the retention date) the explicit
 *      early-deletion override with a mandatory reason.
 *   2. reauth — password re-entry via Cognito; the API independently rejects
 *      stale tokens, so this is enforcement, not decoration.
 *   3. running → done (purge report) or error (with S3-retry path when the
 *      database purge succeeded but file deletion failed).
 */
const HardDeleteModal: React.FC<HardDeleteModalProps> = ({ row, onClose }) => {
    const childName = `${row.first_name} ${row.last_name}`.trim();
    const isDue = row.days_until_hard_delete !== null && row.days_until_hard_delete <= 0;

    const [step, setStep] = useState<Step>('inventory');
    const [inventory, setInventory] = useState<PurgeInventory | null>(null);
    const [inventoryError, setInventoryError] = useState<string | null>(null);
    const [typedName, setTypedName] = useState('');
    const [overrideEarly, setOverrideEarly] = useState(false);
    const [reason, setReason] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [s3RetryUniqueId, setS3RetryUniqueId] = useState<string | null>(null);
    const [report, setReport] = useState<{ counts: Record<string, number>; s3ObjectsDeleted: number } | null>(null);

    useEffect(() => {
        ChildProfileDataService.getPurgeInventory(row.id)
            .then(setInventory)
            .catch(err => {
                console.error('Failed to load purge inventory:', err);
                setInventoryError('Could not load the deletion summary. Close and try again.');
            });
        Auth.currentAuthenticatedUser()
            .then(u => setCurrentEmail(u?.attributes?.email ?? ''))
            .catch(() => setCurrentEmail(''));
    }, [row.id]);

    const nameMatches = typedName.trim().toLowerCase() === childName.toLowerCase();
    const earlyOk = isDue || (overrideEarly && reason.trim() !== '');
    const blocked = !!inventory && inventory.sharedPosts > 0;
    const canContinue = !!inventory && !blocked && nameMatches && earlyOk;

    const executeDelete = async () => {
        setStep('running');
        setError(null);
        try {
            const result = await ChildProfileDataService.hardDelete(row.id, {
                overrideEarly: isDue ? undefined : true,
                reason: reason.trim() || undefined,
            });
            setReport({ counts: result.counts, s3ObjectsDeleted: result.s3ObjectsDeleted });
            setStep('done');
        } catch (err: any) {
            const data = err?.response?.data;
            if (data?.code === 'REAUTH_REQUIRED') {
                setStep('reauth');
                return;
            }
            if (data?.code === 'S3_FAILED' && data?.childUniqueId) {
                setS3RetryUniqueId(data.childUniqueId);
            }
            console.error('Hard delete failed:', err);
            setError(data?.error || 'Permanent deletion failed. See the deletion log.');
            setStep('error');
        }
    };

    const retryS3 = async () => {
        if (!s3RetryUniqueId) return;
        setStep('running');
        setError(null);
        try {
            const result = await ChildProfileDataService.retryHardDeleteS3(s3RetryUniqueId);
            setReport({ counts: {}, s3ObjectsDeleted: result.s3ObjectsDeleted });
            setStep('done');
        } catch (err: any) {
            console.error('S3 retry failed:', err);
            setError(err?.response?.data?.error || 'Deleting stored files failed again. See the deletion log.');
            setStep('error');
        }
    };

    const inventoryRows: { label: string; value: number }[] = inventory ? [
        { label: 'Posts / observations', value: inventory.posts },
        { label: 'Photos & videos', value: inventory.uploads },
        { label: 'Reports', value: inventory.reports },
        { label: 'Documents', value: inventory.documents },
        { label: 'EHCP targets', value: inventory.ehcpTargets },
        { label: 'Attendance records', value: inventory.attendanceLogs },
        { label: 'Schedule entries', value: inventory.scheduleItems },
        { label: 'Emails sent to carers', value: inventory.sentEmails },
        { label: 'Parent/carer account links', value: inventory.parentLinks },
    ] : [];

    return (
        <>
            <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content ivory-panel">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {step === 'done' ? 'Permanently deleted' : `Permanently delete ${childName}`}
                            </h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => onClose(step === 'done')} />
                        </div>

                        {step === 'inventory' && (
                            <>
                                <div className="modal-body">
                                    {inventoryError && <div className="alert alert-danger py-2">{inventoryError}</div>}
                                    {blocked && (
                                        <div className="alert alert-warning py-2">
                                            {inventory!.sharedPosts} post{inventory!.sharedPosts === 1 ? ' is' : 's are'} linked
                                            to another child. Deletion is blocked until those are resolved.
                                        </div>
                                    )}
                                    <p>
                                        This <strong>permanently destroys</strong> everything recorded about{' '}
                                        <strong>{childName}</strong> — including the audit history. It cannot be undone.
                                    </p>
                                    {inventory ? (
                                        <table className="table table-sm mb-3">
                                            <tbody>
                                                {inventoryRows.map(r => (
                                                    <tr key={r.label}>
                                                        <td>{r.label}</td>
                                                        <td className="text-end fw-semibold">{r.value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : !inventoryError && (
                                        <div className="d-flex justify-content-center py-3">
                                            <div className="spinner-border spinner-border-sm text-success" role="status" />
                                        </div>
                                    )}

                                    {!isDue && (
                                        <div className="alert alert-warning py-2">
                                            <div className="form-check mb-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="overrideEarlyCheck"
                                                    checked={overrideEarly}
                                                    onChange={e => setOverrideEarly(e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="overrideEarlyCheck">
                                                    Delete before the retention date
                                                    ({formatDisplayDate(row.hard_delete_due_at)})
                                                </label>
                                            </div>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Reason — e.g. parental erasure request ref 123"
                                                maxLength={500}
                                                value={reason}
                                                onChange={e => setReason(e.target.value)}
                                                disabled={!overrideEarly}
                                            />
                                        </div>
                                    )}

                                    <label className="form-label">
                                        Type the child's full name to confirm: <strong>{childName}</strong>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={typedName}
                                        onChange={e => setTypedName(e.target.value)}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        disabled={!canContinue}
                                        onClick={() => setStep('reauth')}
                                    >
                                        Continue to password check
                                    </button>
                                </div>
                            </>
                        )}

                        {step === 'running' && (
                            <div className="modal-body">
                                <div className="d-flex align-items-center gap-3 py-3">
                                    <div className="spinner-border text-danger" role="status" />
                                    <div>Deleting all records… don't close this window.</div>
                                </div>
                            </div>
                        )}

                        {step === 'done' && report && (
                            <>
                                <div className="modal-body">
                                    <p>
                                        <strong>{childName}</strong>'s records have been permanently deleted.
                                        The deletion log (kept as proof) records who performed this and what was removed.
                                    </p>
                                    <ul className="mb-0">
                                        {Object.keys(report.counts).length > 0 && (
                                            <li>
                                                {Object.values(report.counts).reduce((a, b) => a + b, 0)} database records
                                                removed across {Object.values(report.counts).filter(v => v > 0).length} tables
                                            </li>
                                        )}
                                        <li>{report.s3ObjectsDeleted} stored files deleted</li>
                                    </ul>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-primary" onClick={() => onClose(true)}>
                                        Close
                                    </button>
                                </div>
                            </>
                        )}

                        {step === 'error' && (
                            <>
                                <div className="modal-body">
                                    <div className="alert alert-danger py-2">{error}</div>
                                    {s3RetryUniqueId && (
                                        <p className="mb-0">
                                            The database records were removed, but some stored files remain.
                                            Retry file deletion to finish.
                                        </p>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>
                                        Close
                                    </button>
                                    {s3RetryUniqueId && (
                                        <button type="button" className="btn btn-danger" onClick={retryS3}>
                                            Retry file deletion
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {step === 'reauth' && (
                <PasswordReauthModal
                    defaultEmail={currentEmail}
                    title="Confirm permanent deletion"
                    description={`Enter your password to permanently delete ${childName}'s records. This is recorded in the deletion log as proof of who authorised it.`}
                    confirmLabel="Delete permanently"
                    onConfirmed={() => executeDelete()}
                    onCancel={() => setStep('inventory')}
                />
            )}
        </>
    );
};

export default HardDeleteModal;
